import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { toast } from 'sonner';
import {
  Zap, User, Download, Share2, BarChart3, Globe,
  ShieldCheck, Lock, CheckCircle, ChevronRight, ArrowRight,
  CreditCard, Smartphone, Play, Pause, Volume2, VolumeX,
  AlertTriangle, Star, Fingerprint, Eye, Minus, Plus,
  Loader2, Mail, KeyRound, UserCircle, EyeOff, Tag, Users, Maximize, Minimize,
  MapPin, ExternalLink
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================
// ASSET URLS
// ============================
const ASSETS = {
  cardFront: 'https://customer-assets.emergentagent.com/job_d845b95c-d37c-46f3-ac1d-f2930c337f9a/artifacts/lejwkugb_IK%20%2810%29.png',
  cardBack: 'https://customer-assets.emergentagent.com/job_d845b95c-d37c-46f3-ac1d-f2930c337f9a/artifacts/dtb1b08b_IKATone%20MOndial%20%281%29.png',
  profile: 'https://customer-assets.emergentagent.com/job_d845b95c-d37c-46f3-ac1d-f2930c337f9a/artifacts/v71zy66a_profile.png',
  phoneMockup: 'https://customer-assets.emergentagent.com/job_d845b95c-d37c-46f3-ac1d-f2930c337f9a/artifacts/ypsmultt_Poste%202.png',
  video: 'https://customer-assets.emergentagent.com/job_d845b95c-d37c-46f3-ac1d-f2930c337f9a/artifacts/61pt59ys_IKATone.mp4',
};

// ============================
// TRILINGUAL CONTENT
// ============================
const T = {
  fr: {
    // Hero
    heroTitle: "Montre au monde que tu es Ha\u00eftien",
    heroSub: "Une carte intelligente pour partager ton identit\u00e9 instantan\u00e9ment et repr\u00e9senter Ha\u00efti avec fiert\u00e9.",
    heroOffer: "Paiement unique de 30$ \u2014 1 an offert",
    heroUrgency: "\u00c9dition limit\u00e9e Coupe du Monde 2026",
    heroCta: "Commander ma carte maintenant",
    heroUrgencyMicro: "Stock limit\u00e9 \u2013 \u00e9dition sp\u00e9ciale 2026",
    // What is
    whatTitle: "C'est quoi IKATone ?",
    whatHighlight: "Touche la carte \u2192 ton profil s'ouvre instantan\u00e9ment sur le t\u00e9l\u00e9phone",
    whatHighlightSub: "Pas d'appli. Pas de saisie. Pas de papier.",
    whatP1: "IKATone est une carte intelligente qui remplace les cartes de visite classiques.",
    whatP2: "Au lieu de donner du papier, tu partages ton profil complet en un seul geste.",
    whatP3: "La personne touche ta carte avec son t\u00e9l\u00e9phone et acc\u00e8de imm\u00e9diatement \u00e0 :",
    whatList: ["ton nom", "ton num\u00e9ro", "tes r\u00e9seaux sociaux", "ton profil professionnel"],
    whatOutro: "Sans application. Sans complication.",
    // Mini demo steps
    demoSteps: [
      { icon: "Fingerprint", text: "Touche la carte" },
      { icon: "Smartphone", text: "Le profil s'ouvre" },
      { icon: "Download", text: "Contact sauvegard\u00e9" },
    ],
    // Real life use
    realLifeTitle: "Quand est-ce que tu utilises cette carte ?",
    realLifeList: ["\u00c0 l'\u00e9glise", "En r\u00e9union d'affaires", "Aux \u00e9v\u00e9nements", "En networking", "Quand tu rencontres quelqu'un"],
    // Emotional trigger
    emotionalLine1: "Tu ne donnes pas juste ton contact.",
    emotionalLine2: "Tu repr\u00e9sentes Ha\u00efti",
    // Why
    whyTitle: "Pourquoi tu dois l'avoir aujourd'hui ?",
    whyList: [
      "Tu repr\u00e9sentes Ha\u00efti avec fiert\u00e9",
      "Tu te d\u00e9marques imm\u00e9diatement",
      "Tu simplifies tes \u00e9changes",
      "Tu fais une impression moderne et professionnelle",
    ],
    // How
    howTitle: "Comment \u00e7a marche ?",
    howSteps: [
      { num: "1", text: "Tu re\u00e7ois ta carte" },
      { num: "2", text: "Tu actives ton profil" },
      { num: "3", text: "Tu touches un t\u00e9l\u00e9phone" },
      { num: "4", text: "Ton profil s'affiche instantan\u00e9ment" },
    ],
    // Video
    videoTitle: "Regarde comment \u00e7a fonctionne",
    // Features
    features: [
      { icon: "User", t: "Profil personnalis\u00e9" },
      { icon: "Zap", t: "Partage instantan\u00e9" },
      { icon: "Download", t: "T\u00e9l\u00e9chargement du contact" },
      { icon: "Share2", t: "R\u00e9seaux sociaux" },
      { icon: "BarChart3", t: "Statistiques de vues" },
      { icon: "Globe", t: "Design Ha\u00efti" },
    ],
    // Pricing
    priceTitle: "Aujourd'hui seulement 30$",
    priceIncludes: [
      "Carte premium",
      "1 an d'acc\u00e8s gratuit",
      "Activation imm\u00e9diate",
    ],
    // Payment
    payTitle: "Choisissez votre mode de paiement",
    payInstantTitle: "Paiement imm\u00e9diat",
    payInstantDesc: "Acc\u00e8s instantan\u00e9 apr\u00e8s paiement",
    payInstantBtn: "Payer maintenant",
    payManualTitle: "Paiement manuel",
    payManualDesc: "Activation apr\u00e8s validation",
    payManualBtn: "Soumettre la commande",
    // Trust
    trustSsl: "Paiement s\u00e9curis\u00e9 SSL",
    trustFast: "Activation rapide apr\u00e8s paiement",
    trustCompat: "Compatible tous t\u00e9l\u00e9phones",
    // Urgency
    urgencyTitle: "Edition Mondial 2026 limit\u00e9e",
    urgencyText: "Stock limit\u00e9 \u2014 ne rate pas ta carte",
    // Objection
    objectionQ: "Est-ce que \u00e7a fonctionne sur tous les t\u00e9l\u00e9phones ?",
    objectionA: "Oui. Compatible avec presque tous les smartphones modernes.",
    // Final CTA
    psychClose: "Ils ont d\u00e9j\u00e0 commenc\u00e9\u2026 et toi ?",
    finalText: "Repr\u00e9sente Ha\u00efti aujourd'hui",
    finalBtn: "Commander ma carte maintenant",
    // Footer
    footer: "\u00a9 2026 IKATone. Tous droits r\u00e9serv\u00e9s.",
    // Card label
    cardFrontLabel: "Recto",
    cardBackLabel: "Verso",
    // Checkout
    orderTitle: "Commander ta carte",
    orderCards: "Nombre de cartes",
    orderMin: "Min",
    orderMax: "Max",
    orderEmail: "Email",
    orderPassword: "Mot de passe",
    orderConfirmPwd: "Confirmer le mot de passe",
    orderFirstName: "Pr\u00e9nom",
    orderLastName: "Nom",
    orderTotal: "Total",
    orderPromo: "Code promo",
    orderPromoApply: "Appliquer",
    orderPromoRemove: "Retirer",
    orderDiscount: "R\u00e9duction",
    orderPayStripe: "Payer par carte",
    orderPayStripeDesc: "Acc\u00e8s instantan\u00e9",
    orderPayManual: "Paiement manuel",
    orderPayManualDesc: "Activation apr\u00e8s validation",
    orderSubmitStripe: "Payer maintenant",
    orderSubmitManual: "Soumettre la commande",
    orderProcessing: "Traitement...",
    orderSuccessPending: "Commande soumise ! Activation apr\u00e8s validation.",
    orderFailed: "\u00c9chec de la commande",
    socialProof: "Plus de 500 Ha\u00eftiens utilisent d\u00e9j\u00e0 IKATone dans le monde",
    midCta: "Commander ma carte maintenant",
    pickupTitle: "Choisissez un point de r\u00e9cup\u00e9ration",
    pickupSelect: "Choisir",
    pickupSelected: "S\u00e9lectionn\u00e9",
    pickupMaps: "Voir sur Google Maps",
    pickupRequired: "Veuillez choisir un point de r\u00e9cup\u00e9ration",
  },
  en: {
    heroTitle: "Show the world you are Haitian",
    heroSub: "A powerful smart card to share your identity instantly and proudly represent Haiti.",
    heroOffer: "One-time payment of $30 \u2014 1 year included",
    heroUrgency: "Limited World Cup 2026 Edition",
    heroCta: "Order my card now",
    heroUrgencyMicro: "Limited stock \u2013 special 2026 edition",
    whatTitle: "What is IKATone?",
    whatHighlight: "Touch the card \u2192 your profile opens instantly on the phone",
    whatHighlightSub: "No app. No typing. No paper.",
    whatP1: "IKATone is a smart card that replaces traditional business cards.",
    whatP2: "Instead of paper, you share your full profile instantly.",
    whatP3: "Anyone taps your card and instantly gets:",
    whatList: ["your name", "your phone", "your social media", "your profile"],
    whatOutro: "No app. No friction.",
    demoSteps: [
      { icon: "Fingerprint", text: "Touch the card" },
      { icon: "Smartphone", text: "Profile opens" },
      { icon: "Download", text: "Contact saved" },
    ],
    realLifeTitle: "When do you use this card?",
    realLifeList: ["At church", "At business meetings", "At events", "When networking", "When meeting someone new"],
    emotionalLine1: "You're not just sharing your contact.",
    emotionalLine2: "You're representing Haiti",
    whyTitle: "Why you need it today",
    whyList: [
      "Represent Haiti with pride",
      "Stand out instantly",
      "Share faster",
      "Look modern and professional",
    ],
    howTitle: "How it works",
    howSteps: [
      { num: "1", text: "Receive your card" },
      { num: "2", text: "Activate your profile" },
      { num: "3", text: "Tap a phone" },
      { num: "4", text: "Your profile appears instantly" },
    ],
    videoTitle: "See how it works",
    features: [
      { icon: "User", t: "Personalized profile" },
      { icon: "Zap", t: "Instant sharing" },
      { icon: "Download", t: "Downloadable contact" },
      { icon: "Share2", t: "Social media" },
      { icon: "BarChart3", t: "View statistics" },
      { icon: "Globe", t: "Haiti design" },
    ],
    priceTitle: "Only $30 today",
    priceIncludes: [
      "Premium card",
      "1 year access",
      "Instant activation",
    ],
    payTitle: "Choose your payment method",
    payInstantTitle: "Instant payment",
    payInstantDesc: "Instant access after payment",
    payInstantBtn: "Pay now",
    payManualTitle: "Manual payment",
    payManualDesc: "Activation after approval",
    payManualBtn: "Submit order",
    trustSsl: "SSL Secured Payment",
    trustFast: "Fast activation after payment",
    trustCompat: "All phones compatible",
    urgencyTitle: "Limited World Cup 2026 Edition",
    urgencyText: "Limited stock \u2014 don't miss your card",
    objectionQ: "Does it work on all phones?",
    objectionA: "Yes. Compatible with almost all modern smartphones.",
    psychClose: "They already started\u2026 what about you?",
    finalText: "Represent Haiti today",
    finalBtn: "Order my card now",
    footer: "\u00a9 2026 IKATone. All rights reserved.",
    cardFrontLabel: "Front",
    cardBackLabel: "Back",
    orderTitle: "Order your card",
    orderCards: "Number of cards",
    orderMin: "Min",
    orderMax: "Max",
    orderEmail: "Email",
    orderPassword: "Password",
    orderConfirmPwd: "Confirm password",
    orderFirstName: "First name",
    orderLastName: "Last name",
    orderTotal: "Total",
    orderPromo: "Promo code",
    orderPromoApply: "Apply",
    orderPromoRemove: "Remove",
    orderDiscount: "Discount",
    orderPayStripe: "Pay by card",
    orderPayStripeDesc: "Instant access",
    orderPayManual: "Manual payment",
    orderPayManualDesc: "Activation after approval",
    orderSubmitStripe: "Pay now",
    orderSubmitManual: "Submit order",
    orderProcessing: "Processing...",
    orderSuccessPending: "Order submitted! Activation after approval.",
    orderFailed: "Order failed",
    socialProof: "Over 500 Haitians already using IKATone worldwide",
    midCta: "Order my card now",
    pickupTitle: "Choose a pickup location",
    pickupSelect: "Select",
    pickupSelected: "Selected",
    pickupMaps: "View on Google Maps",
    pickupRequired: "Please choose a pickup location",
  },
  ht: {
    heroTitle: "Montre lemonn ou se Ayisyen",
    heroSub: "Yon kat entelijan pou pataje idantite ou touswit epi reprezante Ayiti ak fy\u00e8te.",
    heroOffer: "Peye yon s\u00e8l fwa 30$ \u2014 1 ane gratis",
    heroUrgency: "Edisyon limite Mondyal 2026",
    heroCta: "K\u00f2mande kat mwen kounye a",
    heroUrgencyMicro: "Estok limite \u2013 edisyon espesyal 2026",
    whatTitle: "Kisa IKATone ye?",
    whatHighlight: "Manyen kat la \u2192 pwofil ou ouvri touswit sou telef\u00f2n nan",
    whatHighlightSub: "Pa aplik. Pa tap. Pa papye.",
    whatP1: "IKATone se yon kat entelijan ki ranplase kat biznis klasik yo.",
    whatP2: "Olye papye, ou pataje tout enf\u00f2masyon ou touswit.",
    whatP3: "Moun nan manyen kat la epi li jwenn:",
    whatList: ["non ou", "telef\u00f2n ou", "rezo sosyal ou"],
    whatOutro: "San aplikasyon. San konplikasyon.",
    demoSteps: [
      { icon: "Fingerprint", text: "Manyen kat la" },
      { icon: "Smartphone", text: "Pwofil ouvri" },
      { icon: "Download", text: "Kontak sove" },
    ],
    realLifeTitle: "Kil\u00e8 ou itilize kat sa a?",
    realLifeList: ["Nan legliz", "Nan reyinyon biznis", "Nan evennman", "Nan reseautage", "L\u00e8 ou rankontre yon moun"],
    emotionalLine1: "Ou pa jis bay kontak ou.",
    emotionalLine2: "Ou reprezante Ayiti",
    whyTitle: "Poukisa ou bezwen li jodi a",
    whyList: [
      "Ou reprezante Ayiti ak fy\u00e8te",
      "Ou diferan de l\u00f2t yo",
      "Ou pataje pi rapid",
      "Ou par\u00e8t pwofesyon\u00e8l",
    ],
    howTitle: "Kijan li mache",
    howSteps: [
      { num: "1", text: "Ou resevwa kat la" },
      { num: "2", text: "Ou aktive pwofil ou" },
      { num: "3", text: "Ou manyen telef\u00f2n lan" },
      { num: "4", text: "Tout bagay par\u00e8t touswit" },
    ],
    videoTitle: "Gade kijan li mache",
    features: [
      { icon: "User", t: "Pwofil p\u00e8sonalize" },
      { icon: "Zap", t: "Pataj imedya" },
      { icon: "Download", t: "Kontak telechajab" },
      { icon: "Share2", t: "Rezo sosyal" },
      { icon: "BarChart3", t: "Estatistik" },
      { icon: "Globe", t: "Dezayn Ayiti" },
    ],
    priceTitle: "Jodi a s\u00e8lman 30$",
    priceIncludes: [
      "Kat premium",
      "1 ane gratis",
      "Aktivasyon touswit",
    ],
    payTitle: "Chwazi fason pou peye",
    payInstantTitle: "Peye kounye a",
    payInstantDesc: "Aks\u00e8 touswit apr\u00e8 peyman",
    payInstantBtn: "Peye kounye a",
    payManualTitle: "Peye pita",
    payManualDesc: "Aktive apr\u00e8 validasyon",
    payManualBtn: "Soum\u00e8t k\u00f2mand",
    trustSsl: "Peyman sekirize SSL",
    trustFast: "Aktivasyon rapid apr\u00e8 peyman",
    trustCompat: "Konpatib ak tout telef\u00f2n",
    urgencyTitle: "Edisyon Limite Koup di Monn 2026",
    urgencyText: "Estok limite \u2014 pa rate kat ou",
    objectionQ: "\u00c8ske li mache sou tout telef\u00f2n?",
    objectionA: "Wi. Konpatib ak preske tout smartphone mod\u00e8n yo.",
    psychClose: "Yo d\u00e9ja kòmanse\u2026 e ou menm?",
    finalText: "Reprezante Ayiti jodi a",
    finalBtn: "K\u00f2mande kat mwen kounye a",
    footer: "\u00a9 2026 IKATone. Tout dwa rez\u00e8ve.",
    cardFrontLabel: "Devan",
    cardBackLabel: "D\u00e8y\u00e8",
    orderTitle: "K\u00f2mande kat ou",
    orderCards: "Kantite kat",
    orderMin: "Min",
    orderMax: "Maks",
    orderEmail: "Imel",
    orderPassword: "Modpas",
    orderConfirmPwd: "Konfime modpas",
    orderFirstName: "Premye non",
    orderLastName: "Siyati",
    orderTotal: "Total",
    orderPromo: "K\u00f2d pwomosyon",
    orderPromoApply: "Aplike",
    orderPromoRemove: "Retire",
    orderDiscount: "Rediksyon",
    orderPayStripe: "Peye ak kat",
    orderPayStripeDesc: "Aks\u00e8 touswit",
    orderPayManual: "Peyman manyel",
    orderPayManualDesc: "Aktive apr\u00e8 validasyon",
    orderSubmitStripe: "Peye kounye a",
    orderSubmitManual: "Soum\u00e8t k\u00f2mand",
    orderProcessing: "Tretman...",
    orderSuccessPending: "K\u00f2mand soum\u00e8t! Aktivasyon apr\u00e8 validasyon.",
    orderFailed: "K\u00f2mand echwe",
    socialProof: "Plis pase 500 Ayisyen d\u00e9ja ap itilize IKATone nan lemonn",
    midCta: "K\u00f2mande kat mwen kounye a",
    pickupTitle: "Chwazi yon kote pou pran kat ou",
    pickupSelect: "Chwazi",
    pickupSelected: "Chwazi",
    pickupMaps: "Gade sou Google Maps",
    pickupRequired: "Tanpri chwazi yon kote pou pran kat ou",
  },
};

const ICONS_MAP = { Zap, User, Download, Share2, BarChart3, Globe };

// ============================
// Reveal on scroll wrapper
// ============================
function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ============================
// Language Switcher
// ============================
function LangSwitch({ lang, set }) {
  const opts = [
    { id: 'fr', label: 'FR', flag: '\ud83c\uddeb\ud83c\uddf7' },
    { id: 'en', label: 'EN', flag: '\ud83c\uddfa\ud83c\uddf8' },
    { id: 'ht', label: 'HT', flag: '\ud83c\udded\ud83c\uddf9' },
  ];
  return (
    <div className="fixed top-4 right-4 z-50 flex items-center backdrop-blur-xl bg-black/40 border border-white/15 rounded-full p-1 gap-0.5 shadow-lg shadow-black/30" data-testid="language-switcher">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => set(o.id)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all duration-200 flex items-center gap-1 ${
            lang === o.id
              ? 'bg-[#D62828] text-white shadow-md shadow-red-900/40'
              : 'text-white/60 hover:text-white hover:bg-white/10'
          }`}
          data-testid={`lang-${o.id}`}
        >
          <span>{o.flag}</span> {o.label}
        </button>
      ))}
    </div>
  );
}

// ============================
// Video Player
// ============================
function VideoPlayer() {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggle = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); }
    else { videoRef.current.play(); }
    setPlaying(!playing);
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  return (
    <div ref={containerRef} className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 group max-w-2xl mx-auto bg-black">
      <video
        ref={videoRef}
        src={ASSETS.video}
        muted={muted}
        loop
        playsInline
        preload="metadata"
        className="w-full aspect-video object-cover"
        onClick={toggle}
        data-testid="demo-video"
      />
      {/* Overlay controls */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {!playing && (
          <div className="w-16 h-16 bg-[#D62828] rounded-full flex items-center justify-center shadow-lg shadow-red-900/40 pointer-events-auto cursor-pointer" onClick={toggle}>
            <Play className="w-7 h-7 text-white ml-1" />
          </div>
        )}
      </div>
      {/* Bottom bar */}
      <div className="absolute bottom-3 right-3 flex items-center gap-2">
        {playing && (
          <button onClick={toggle} className="w-8 h-8 bg-black/60 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-black/80 transition">
            <Pause className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => { setMuted(!muted); if(videoRef.current) videoRef.current.muted = !muted; }} className="w-8 h-8 bg-black/60 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-black/80 transition">
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <button onClick={toggleFullscreen} className="w-8 h-8 bg-black/60 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-black/80 transition" data-testid="fullscreen-btn">
          {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ============================
// MAIN PAGE
// ============================
export default function HaitiMondial() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('fr');
  const t = T[lang];
  const [flipped, setFlipped] = useState(false);
  const orderRef = useRef(null);

  // Set page title & meta
  useEffect(() => {
    document.title = 'IKATone Haiti Mondial | Coupe du Monde 2026';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', t.heroSub);
  }, [lang, t.heroSub]);

  // ---- Inline checkout state ----
  const [quantity, setQuantity] = useState(1);
  const [paymentMode, setPaymentMode] = useState('stripe');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [errors, setErrors] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponResult, setCouponResult] = useState(null);
  const [orderCalc, setOrderCalc] = useState(null);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState(null);

  // Calculate order on quantity change
  useEffect(() => {
    const calc = async () => {
      try {
        const res = await fetch(`${API}/checkout/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product_type: 'haiti_mondial', quantity, plan_choice: 'pro' })
        });
        if (res.ok) setOrderCalc(await res.json());
      } catch (e) { console.error('Price calc error:', e); }
    };
    calc();
  }, [quantity]);

  // Fetch pickup points
  useEffect(() => {
    fetch(`${API}/pickup-points`).then(r => r.json()).then(setPickupPoints).catch(() => {});
  }, []);

  // Helper: XHR-based POST to avoid fetch interceptor consuming response body
  const xhrPost = (url, body) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = () => {
      try { resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data: JSON.parse(xhr.responseText) }); }
      catch { resolve({ ok: false, status: xhr.status, data: { detail: xhr.responseText } }); }
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(typeof body === 'string' ? body : JSON.stringify(body));
  });

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponApplying(true);
    try {
      const params = new URLSearchParams({ code: couponCode.trim(), product_type: 'haiti_mondial', plan_type: 'pro', payment_mode: paymentMode });
      const { data } = await xhrPost(`${API}/coupons/validate?${params}`, '{}');
      if (data.valid) { setCouponResult(data); toast.success(lang === 'en' ? 'Coupon applied!' : 'Coupon appliqu\u00e9 !'); }
      else { setCouponResult({ valid: false }); toast.error(data.message || 'Coupon invalide'); }
    } catch { toast.error('Erreur serveur'); }
    finally { setCouponApplying(false); }
  };

  const getTotal = () => {
    const base = 30 * quantity;
    if (!couponResult?.valid) return base;
    const c = couponResult.coupon;
    if (c.discount_type === 'percentage') return Math.max(0, base * (1 - c.discount_value / 100));
    return Math.max(0, base - c.discount_value);
  };

  const getDiscount = () => (!couponResult?.valid) ? 0 : (30 * quantity) - getTotal();

  const validate = () => {
    const e = {};
    if (!email) e.email = 'Required'; else if (!/\S+@\S+\.\S+/.test(email)) e.email = 'Invalid';
    if (!password) e.password = 'Required'; else if (password.length < 6) e.password = 'Min 6 chars';
    if (password !== confirmPassword) e.confirmPassword = 'Mismatch';
    if (!firstName) e.firstName = 'Required';
    if (!lastName) e.lastName = 'Required';
    if (paymentMode === 'manual' && pickupPoints.length > 0 && !selectedPickup) e.pickup = t.pickupRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      if (paymentMode === 'stripe') {
        const { ok, data } = await xhrPost(`${API}/checkout/stripe/guest-session`, {
          email, password, first_name: firstName, last_name: lastName,
          product_type: 'haiti_mondial', quantity, color: null,
          business_sigle: null, plan_choice: 'pro',
          origin_url: window.location.origin,
          coupon_code: couponResult?.valid ? couponCode.trim() : null
        });
        if (ok) {
          window.location.href = data.checkout_url;
          return;
        }
        toast.error(data.detail || t.orderFailed);
      } else {
        const params = new URLSearchParams({ email, password, first_name: firstName, last_name: lastName });
        const { ok, data } = await xhrPost(`${API}/checkout/order?${params.toString()}`, {
          product_type: 'haiti_mondial', quantity, color: null,
          business_sigle: null, plan_choice: 'pro', payment_mode: 'manual',
          coupon_code: couponResult?.valid ? couponCode.trim() : null,
          pickup_point_id: selectedPickup || null
        });
        if (ok) {
          if (data.access_token) localStorage.setItem('token', data.access_token);
          toast.success(t.orderSuccessPending);
          navigate('/checkout/pending', { state: { orderId: data.order_id, profiles: data.profiles, total: data.total, email, message: data.message } });
        } else {
          toast.error(data.detail || t.orderFailed);
        }
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(t.orderFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToOrder = () => orderRef.current?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="bg-[#020817] text-white overflow-x-hidden" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <LangSwitch lang={lang} set={setLang} />

      {/* ============ HERO ============ */}
      <section className="relative min-h-[94vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* BG decorations */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#003DA5]/25 via-[#020817] to-[#D62828]/15" />
          <div className="absolute top-10 left-[8%] w-80 h-80 bg-[#003DA5]/20 rounded-full blur-[130px]" />
          <div className="absolute bottom-10 right-[8%] w-96 h-96 bg-[#D62828]/15 rounded-full blur-[140px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-12 w-full py-16 md:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left copy */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
              {/* Urgency pill */}
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-5 border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full">
                <AlertTriangle className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-semibold tracking-wide">{t.heroUrgency}</span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-[3.8rem] font-black tracking-tight leading-[1.08] mb-5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
                data-testid="hero-title"
              >
                {t.heroTitle} <span className="inline-block text-4xl sm:text-5xl">&#127469;&#127481;</span>
              </h1>

              <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-5 max-w-xl">{t.heroSub}</p>

              <div className="flex items-center gap-3 mb-7 p-3 bg-white/5 border border-white/10 rounded-xl max-w-md">
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4AF37] font-extrabold text-lg">$</span>
                </div>
                <p className="text-sm sm:text-base text-slate-200 font-medium">{t.heroOffer}</p>
              </div>

              <button
                onClick={scrollToOrder}
                className="group inline-flex items-center gap-3 bg-[#D62828] hover:bg-[#B91C1C] text-white font-bold text-base sm:text-lg py-4 px-8 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-red-900/30 hover:-translate-y-0.5"
                data-testid="hero-cta-button"
              >
                {t.heroCta} <span>&#127469;&#127481;</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-xs text-[#D4AF37]/80 mt-3 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" /> {t.heroUrgencyMicro}
              </p>
            </motion.div>

            {/* Right — flippable card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="flex flex-col items-center"
            >
              <div
                className="relative cursor-pointer perspective-[1000px] w-full max-w-md"
                onClick={() => setFlipped(!flipped)}
                data-testid="card-flipper"
              >
                <div className="absolute -inset-4 bg-gradient-to-br from-[#003DA5]/25 to-[#D62828]/25 rounded-3xl blur-2xl" />
                <div className={`relative transition-transform duration-700 preserve-3d ${flipped ? 'rotate-y-180' : ''}`} style={{ transformStyle: 'preserve-3d' }}>
                  {/* Front */}
                  <img
                    src={ASSETS.cardFront}
                    alt="IKATone Haiti Mondial - Recto"
                    className="w-full rounded-2xl shadow-2xl shadow-black/40"
                    style={{ backfaceVisibility: 'hidden' }}
                    loading="eager"
                  />
                  {/* Back */}
                  <img
                    src={ASSETS.cardBack}
                    alt="IKATone Haiti Mondial - Verso"
                    className="w-full rounded-2xl shadow-2xl shadow-black/40 absolute inset-0"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                    loading="eager"
                  />
                </div>
              </div>
              <p className="text-xs text-white mt-3 text-center">
                {flipped ? t.cardBackLabel : t.cardFrontLabel} &middot; {lang === 'ht' ? 'Klike pou vire' : lang === 'en' ? 'Click to flip' : 'Cliquez pour retourner'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ WHAT IS IKATONE ============ */}
      <section className="py-20 md:py-28 bg-[#0F172A]" data-testid="what-section">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-8 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.whatTitle}
            </h2>
          </Reveal>

          {/* Image en grand plan — cliquable */}
          <Reveal delay={0.1} className="flex justify-center mb-10">
            <div
              className="relative cursor-pointer group max-w-2xl w-full"
              onClick={() => setLightbox(true)}
              data-testid="what-image"
            >
              <div className="absolute -inset-3 bg-gradient-to-br from-[#003DA5]/20 to-[#D62828]/20 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity" />
              <img
                src={ASSETS.phoneMockup}
                alt="IKATone Haiti Mondial"
                className="relative w-full rounded-2xl shadow-2xl shadow-black/40 border border-white/10 group-hover:scale-[1.01] transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="bg-black/50 backdrop-blur-sm rounded-full p-3">
                  <Eye className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* Texte explicatif */}
          <div className="max-w-3xl mx-auto">
            <Reveal delay={0.2}>
              {/* Highlighted block */}
              <div className="bg-[#D62828]/10 border border-[#D62828]/30 rounded-xl p-5 mb-6" data-testid="what-highlight">
                <p className="text-lg sm:text-xl font-bold text-white mb-1">{t.whatHighlight}</p>
                <p className="text-sm text-slate-300">{t.whatHighlightSub}</p>
              </div>

              {/* Mini demo steps */}
              <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="demo-steps">
                {t.demoSteps.map((step, i) => {
                  const StepIcon = step.icon === 'Fingerprint' ? Fingerprint : step.icon === 'Smartphone' ? Smartphone : Download;
                  return (
                    <React.Fragment key={i}>
                      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                        <StepIcon className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-sm font-medium text-slate-200">{step.text}</span>
                      </div>
                      {i < t.demoSteps.length - 1 && <ArrowRight className="w-4 h-4 text-slate-500 flex-shrink-0" />}
                    </React.Fragment>
                  );
                })}
              </div>

              <p className="text-lg text-slate-300 mb-3">{t.whatP1}</p>
              <p className="text-lg text-slate-300 mb-5">{t.whatP2}</p>
              <p className="text-lg text-slate-200 font-medium mb-3">{t.whatP3}</p>
              <ul className="space-y-2 mb-6">
                {t.whatList.map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-200 text-lg">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-xl font-bold text-white">{t.whatOutro}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ VIDEO DEMO ============ */}
      <section className="py-20 md:py-28 bg-[#020817]" data-testid="video-section">
        <div className="max-w-4xl mx-auto px-5 md:px-12 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.videoTitle}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <VideoPlayer />
          </Reveal>
        </div>
      </section>

      {/* ============ WHY YOU NEED IT ============ */}
      <section className="py-20 md:py-28 bg-[#0F172A]" data-testid="why-section">
        <div className="max-w-4xl mx-auto px-5 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-10 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.whyTitle}
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {t.whyList.map((item, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex items-start gap-4 p-5 bg-white/5 border border-white/10 rounded-xl hover:border-[#D62828]/40 transition-colors">
                  <div className="w-10 h-10 bg-[#D62828]/15 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Star className="w-5 h-5 text-[#D62828]" />
                  </div>
                  <p className="text-lg text-slate-200 font-medium">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ REAL LIFE USE ============ */}
      <section className="py-16 md:py-20 bg-[#020817]" data-testid="reallife-section">
        <div className="max-w-4xl mx-auto px-5 md:px-12 text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.realLifeTitle}
            </h2>
          </Reveal>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {t.realLifeList.map((item, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <span className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-sm sm:text-base text-slate-200 font-medium hover:border-[#D4AF37]/40 transition-colors">
                  {item}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EMOTIONAL TRIGGER ============ */}
      <section className="py-16 md:py-20 bg-[#0F172A] border-y border-white/5" data-testid="emotional-section">
        <div className="max-w-3xl mx-auto px-5 md:px-12 text-center">
          <Reveal>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-300 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.emotionalLine1}
            </p>
            <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.emotionalLine2} <span className="text-3xl sm:text-4xl">&#127469;&#127481;</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-20 md:py-28 bg-[#020817]" data-testid="how-section">
        <div className="max-w-5xl mx-auto px-5 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-12 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.howTitle}
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {t.howSteps.map((step, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="text-center p-6 bg-slate-900/50 border border-white/10 rounded-xl hover:border-[#D4AF37]/40 transition-colors">
                  <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-[#003DA5] to-[#D62828] rounded-full flex items-center justify-center text-white font-black text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {step.num}
                  </div>
                  <p className="text-base text-slate-200 font-medium">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MID-PAGE CTA + SOCIAL PROOF ============ */}
      <section className="py-16 bg-gradient-to-b from-[#020817] to-[#0F172A]" data-testid="mid-cta-section">
        <div className="max-w-3xl mx-auto px-5 md:px-12 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 rounded-full mb-6" data-testid="social-proof">
              <div className="flex -space-x-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="w-7 h-7 rounded-full border-2 border-[#0F172A] bg-gradient-to-br from-[#003DA5] to-[#D62828]" />
                ))}
              </div>
              <p className="text-sm sm:text-base font-bold text-white">{t.socialProof}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <button
              onClick={scrollToOrder}
              className="group inline-flex items-center gap-3 bg-[#D62828] hover:bg-[#B91C1C] text-white font-bold text-lg py-4 px-10 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-red-900/30 hover:-translate-y-0.5"
              data-testid="mid-cta-button"
            >
              {t.midCta} <span>&#127469;&#127481;</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-xs text-[#D4AF37]/80 mt-3 flex items-center justify-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {t.heroUrgencyMicro}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="py-20 md:py-28 bg-[#0F172A]" data-testid="features-section">
        <div className="max-w-5xl mx-auto px-5 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {t.features.map((feat, i) => {
              const Icon = ICONS_MAP[feat.icon] || Globe;
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#D62828]/40 transition-all" data-testid="feature-card">
                    <div className="w-10 h-10 bg-[#D62828]/15 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#D62828]" />
                    </div>
                    <span className="text-sm sm:text-base font-medium text-slate-200">{feat.t}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ PROFILE PREVIEW ============ */}
      <section className="py-20 md:py-28 bg-[#020817]" data-testid="profile-section">
        <div className="max-w-7xl mx-auto px-5 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal className="flex justify-center order-2 lg:order-1">
              <div className="relative max-w-sm">
                <div className="absolute -inset-4 bg-[#003DA5]/10 rounded-3xl blur-3xl" />
                <img src={ASSETS.profile} alt="Profile preview" className="relative w-full rounded-2xl shadow-2xl shadow-black/40 border border-white/10" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.15} className="order-1 lg:order-2">
              <div className="space-y-4">
                <Fingerprint className="w-10 h-10 text-[#D62828] mb-2" />
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {lang === 'ht' ? 'Pwofil ou an liy' : lang === 'en' ? 'Your online profile' : 'Ton profil en ligne'}
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed">
                  {lang === 'ht'
                    ? 'Tout moun ki manyen kat ou wè pwofil tankou sa a. Enfòmasyon ou, rezo sosyal ou, tout nan yon sèl kote.'
                    : lang === 'en'
                    ? 'Everyone who taps your card sees a profile like this. Your info, your socials, all in one place.'
                    : 'Toute personne qui touche ta carte voit un profil comme celui-ci. Tes infos, tes r\u00e9seaux, tout au m\u00eame endroit.'}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="py-20 md:py-28 bg-[#0F172A] border-y border-white/10" data-testid="pricing-section">
        <div className="max-w-lg mx-auto px-5 md:px-12 text-center">
          <Reveal>
            <p className="text-[#D4AF37] text-sm font-bold tracking-[0.2em] uppercase mb-3">PRO PLAN</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.priceTitle}
            </h2>
            <div className="w-16 h-1 bg-[#D62828] mx-auto rounded-full mb-8" />
            <div className="space-y-3 mb-8">
              {t.priceIncludes.map((inc, i) => (
                <div key={i} className="flex items-center justify-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00C48C]" />
                  <span className="text-lg text-slate-200">{inc}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ ORDER FORM (INLINE CHECKOUT) ============ */}
      <section ref={orderRef} className="py-20 md:py-28 bg-[#020817]" data-testid="order-section" id="order">
        <div className="max-w-2xl mx-auto px-5 md:px-12">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-2 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.orderTitle} <span>&#127469;&#127481;</span>
            </h2>
            <p className="text-center text-slate-400 mb-8 text-sm">{t.heroUrgency}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={handleOrder} className="bg-slate-900/60 border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="order-form">
              {/* Quantity */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-2 block">{t.orderCards}</label>
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => quantity > 1 && setQuantity(quantity - 1)} className="w-10 h-10 bg-white/10 hover:bg-white/15 rounded-lg flex items-center justify-center text-white transition" data-testid="qty-minus">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-2xl font-bold text-white w-16 text-center" data-testid="qty-value">{quantity}</span>
                  <button type="button" onClick={() => quantity < 1000 && setQuantity(quantity + 1)} className="w-10 h-10 bg-white/10 hover:bg-white/15 rounded-lg flex items-center justify-center text-white transition" data-testid="qty-plus">
                    <Plus className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-500 ml-2">{t.orderMin}: 1 &middot; {t.orderMax}: 1000</span>
                </div>
              </div>

              {/* Name */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">{t.orderFirstName}</label>
                  <div className="relative">
                    <UserCircle className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className={`w-full pl-9 pr-3 py-2.5 bg-white/5 border ${errors.firstName ? 'border-red-500' : 'border-white/10'} rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#D62828]/60`} data-testid="input-firstname" />
                  </div>
                  {errors.firstName && <p className="text-xs text-red-400 mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">{t.orderLastName}</label>
                  <div className="relative">
                    <UserCircle className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className={`w-full pl-9 pr-3 py-2.5 bg-white/5 border ${errors.lastName ? 'border-red-500' : 'border-white/10'} rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#D62828]/60`} data-testid="input-lastname" />
                  </div>
                  {errors.lastName && <p className="text-xs text-red-400 mt-1">{errors.lastName}</p>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">{t.orderEmail}</label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={`w-full pl-9 pr-3 py-2.5 bg-white/5 border ${errors.email ? 'border-red-500' : 'border-white/10'} rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#D62828]/60`} data-testid="input-email" />
                </div>
                {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">{t.orderPassword}</label>
                  <div className="relative">
                    <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className={`w-full pl-9 pr-9 py-2.5 bg-white/5 border ${errors.password ? 'border-red-500' : 'border-white/10'} rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#D62828]/60`} data-testid="input-password" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-400 mt-1">{errors.password}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300 mb-1 block">{t.orderConfirmPwd}</label>
                  <div className="relative">
                    <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input type={showPwd ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={`w-full pl-9 pr-3 py-2.5 bg-white/5 border ${errors.confirmPassword ? 'border-red-500' : 'border-white/10'} rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#D62828]/60`} data-testid="input-confirm-pwd" />
                  </div>
                  {errors.confirmPassword && <p className="text-xs text-red-400 mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>

              {/* Coupon */}
              <div>
                <label className="text-sm font-medium text-slate-300 mb-1 block">{t.orderPromo}</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input type="text" value={couponCode} onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }} className="w-full pl-9 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#D62828]/60" placeholder="CODE" data-testid="input-coupon" />
                  </div>
                  {couponResult?.valid ? (
                    <button type="button" onClick={() => { setCouponCode(''); setCouponResult(null); }} className="px-4 py-2.5 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30 transition">{t.orderPromoRemove}</button>
                  ) : (
                    <button type="button" onClick={applyCoupon} disabled={couponApplying || !couponCode.trim()} className="px-4 py-2.5 bg-white/10 text-white rounded-lg text-sm font-medium hover:bg-white/15 transition disabled:opacity-40">{couponApplying ? '...' : t.orderPromoApply}</button>
                  )}
                </div>
                {couponResult?.valid && <p className="text-xs text-[#00C48C] mt-1">{t.orderDiscount}: -${getDiscount().toFixed(2)}</p>}
              </div>

              {/* Divider */}
              <div className="border-t border-white/10 pt-4">
                {/* Order summary */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-300">{t.orderTotal}</span>
                  <div className="text-right">
                    {getDiscount() > 0 && <p className="text-xs text-slate-500 line-through">${(30 * quantity).toFixed(2)}</p>}
                    <p className="text-2xl font-black text-white" data-testid="order-total">${getTotal().toFixed(2)}</p>
                  </div>
                </div>

                {/* Payment mode toggle */}
                <div className="grid grid-cols-2 gap-2 mb-5">
                  <button
                    type="button"
                    onClick={() => setPaymentMode('stripe')}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${paymentMode === 'stripe' ? 'border-[#D62828] bg-[#D62828]/10' : 'border-white/10 hover:border-white/20'}`}
                    data-testid="pay-mode-stripe"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Zap className="w-4 h-4 text-[#D62828]" />
                      <span className="text-sm font-bold text-white">{t.orderPayStripe}</span>
                    </div>
                    <p className="text-xs text-slate-400">{t.orderPayStripeDesc}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMode('manual')}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${paymentMode === 'manual' ? 'border-amber-500 bg-amber-500/10' : 'border-white/10 hover:border-white/20'}`}
                    data-testid="pay-mode-manual"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-bold text-white">{t.orderPayManual}</span>
                    </div>
                    <p className="text-xs text-slate-400">{t.orderPayManualDesc}</p>
                  </button>
                </div>

                {/* Pickup Points (manual only) */}
                {paymentMode === 'manual' && pickupPoints.length > 0 && (
                  <div className="pt-2" data-testid="pickup-section">
                    <label className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#D4AF37]" />
                      {t.pickupTitle}
                    </label>
                    <div className="space-y-2 mt-2">
                      {pickupPoints.map((pp) => (
                        <div
                          key={pp.id}
                          onClick={() => setSelectedPickup(pp.id)}
                          className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedPickup === pp.id
                              ? 'border-[#D62828] bg-[#D62828]/10'
                              : 'border-white/10 hover:border-white/20'
                          }`}
                          data-testid={`pickup-${pp.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${selectedPickup === pp.id ? 'bg-[#D62828]/20' : 'bg-white/5'}`}>
                              <MapPin className={`w-4 h-4 ${selectedPickup === pp.id ? 'text-[#D62828]' : 'text-slate-400'}`} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-white">{pp.name}</p>
                              {pp.address && <p className="text-xs text-slate-400">{pp.address}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={pp.google_maps_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs text-[#D4AF37] hover:underline flex items-center gap-1"
                              data-testid={`maps-${pp.id}`}
                            >
                              <ExternalLink className="w-3 h-3" /> {t.pickupMaps}
                            </a>
                            {selectedPickup === pp.id && (
                              <span className="text-xs font-bold text-[#D62828] bg-[#D62828]/10 px-2 py-0.5 rounded">{t.pickupSelected}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors.pickup && <p className="text-xs text-red-400 mt-1">{errors.pickup}</p>}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#D62828] hover:bg-[#B91C1C] text-white font-bold text-lg py-4 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-red-900/30 flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="order-submit-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> {t.orderProcessing}</>
                  ) : (
                    <>{paymentMode === 'stripe' ? t.orderSubmitStripe : t.orderSubmitManual} <ArrowRight className="w-5 h-5" /></>
                  )}
                </button>

                {/* Trust inline */}
                <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> {t.trustSsl}</span>
                  <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {t.trustFast}</span>
                </div>
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* ============ TRUST ============ */}
      <section className="py-12 bg-[#0F172A] border-y border-white/5" data-testid="trust-section">
        <div className="max-w-3xl mx-auto px-5 md:px-12">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Lock className="w-4 h-4" />
              <span>{t.trustSsl}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Zap className="w-4 h-4" />
              <span>{t.trustFast}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Smartphone className="w-4 h-4" />
              <span>{t.trustCompat}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ OBJECTION HANDLING ============ */}
      <section className="py-16 md:py-20 bg-[#020817]" data-testid="objection-section">
        <div className="max-w-2xl mx-auto px-5 md:px-12 text-center">
          <Reveal>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <p className="text-lg font-bold text-white mb-3">{t.objectionQ}</p>
              <p className="text-base text-slate-300">{t.objectionA}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ URGENCY + FINAL CTA ============ */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="final-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#020817] via-[#020817] to-[#D62828]/15" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D62828] to-transparent" />

        <div className="relative max-w-3xl mx-auto px-5 md:px-12 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-5 border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full">
              <AlertTriangle className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-semibold">{t.urgencyText}</span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.urgencyTitle}
            </h2>
          </Reveal>

          {/* Psychological close */}
          <Reveal delay={0.15}>
            <p className="text-lg text-slate-400 italic mb-4">{t.psychClose}</p>
          </Reveal>

          <Reveal delay={0.2}>
            <p className="text-xl sm:text-2xl text-slate-300 mb-8">
              {t.finalText} <span>&#127469;&#127481;</span>
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <button
              onClick={scrollToOrder}
              className="group inline-flex items-center gap-3 bg-[#D62828] hover:bg-[#B91C1C] text-white font-bold text-lg py-5 px-10 rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-red-900/30 hover:-translate-y-0.5"
              data-testid="final-cta-button"
            >
              {t.finalBtn} <span>&#127469;&#127481;</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-xs text-[#D4AF37]/80 mt-3 flex items-center justify-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {t.heroUrgencyMicro}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ LIGHTBOX ============ */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightbox(false)}
          data-testid="lightbox"
        >
          <img
            src={ASSETS.phoneMockup}
            alt="IKATone Haiti Mondial"
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition"
            data-testid="lightbox-close"
          >
            <span className="text-2xl leading-none">&times;</span>
          </button>
        </div>
      )}

      {/* ============ FOOTER ============ */}
      <footer className="py-6 border-t border-white/5 text-center">
        <p className="text-xs text-slate-500">{t.footer}</p>
      </footer>
    </div>
  );
}
